import { SystemSetting } from '@/api/settings';
import { AlertTriangle } from 'lucide-react';
import { SettingCard } from './SettingCard';

interface SettingsListProps {
  settings: SystemSetting[];
  category: 'system' | 'security' | 'ui' | 'registration' | 'rate-limits';
  canManage: boolean;
}

const REQUIRED_SECURITY_SETTINGS: Array<{
  key: string;
  defaultValue: number;
  description: string;
}> = [
  {
    key: 'security.login.maxAttempts',
    defaultValue: 5,
    description: 'Intentos de login antes de bloquear cuenta (3-100)',
  },
  {
    key: 'security.login.blockDurationMinutes',
    defaultValue: 60,
    description: 'Tiempo que dura el bloqueo tras exceder intentos fallidos (5-1440)',
  },
  {
    key: 'security.session.timeoutMinutes',
    defaultValue: 60,
    description: 'Tiempo de expiracion de sesion en minutos (2-1440)',
  },
  {
    key: 'security.magicLink.expiryHours',
    defaultValue: 48,
    description: 'Horas de validez para magic links de ponentes',
  },
];

const REQUIRED_RATE_LIMIT_SETTINGS: Array<{
  key: string;
  defaultValue: number;
  description: string;
}> = [
  {
    key: 'RATE_LIMIT_CURP_MAX',
    defaultValue: 10500,
    description: 'Validacion de CURP. Se reinicia cada 15 minutos.',
  },
  {
    key: 'RATE_LIMIT_SIAE_MAX',
    defaultValue: 15000,
    description: 'Validacion SIAE. Se reinicia cada 5 minutos.',
  },
  {
    key: 'RATE_LIMIT_LOGIN_MAX',
    defaultValue: 30000,
    description: 'Login y submit final de registro. Se reinicia cada 15 minutos.',
  },
  {
    key: 'RATE_LIMIT_TRACKING_MAX',
    defaultValue: 10000,
    description: 'Consulta de tracking de folio. Se reinicia cada 15 minutos.',
  },
  {
    key: 'RATE_LIMIT_RESEND_MAX',
    defaultValue: 200,
    description: 'Reenvio de correos/credenciales. Se reinicia cada 30 minutos.',
  },
];

const REQUIRED_REGISTRATION_SETTINGS: Array<{
  key: string;
  defaultValue: boolean | string;
  type: SystemSetting['type'];
  description: string;
}> = [
  {
    key: 'REGISTRATION_ATTENDEE_ENABLED',
    defaultValue: true,
    type: 'BOOLEAN',
    description: 'Habilita o bloquea el registro publico para asistentes.',
  },
  {
    key: 'REGISTRATION_SPEAKER_ENABLED',
    defaultValue: true,
    type: 'BOOLEAN',
    description: 'Habilita o bloquea el registro publico para ponentes.',
  },
  {
    key: 'REGISTRATION_ATTENDEE_DEADLINE',
    defaultValue: '',
    type: 'STRING',
    description: 'Fecha límite global para registro de asistentes. Se captura en horario local de México y se guarda en UTC.',
  },
  {
    key: 'REGISTRATION_SPEAKER_DEADLINE',
    defaultValue: '',
    type: 'STRING',
    description: 'Fecha límite global para registro de ponentes. Se captura en horario local de México y se guarda en UTC.',
  },
];

function buildVirtualSetting(
  key: string,
  defaultValue: number | boolean | string,
  type: SystemSetting['type'],
  description: string
): SystemSetting {
  return {
    id: `virtual-${key}`,
    scope: 'GLOBAL',
    editionId: null,
    eventId: null,
    key,
    value: defaultValue,
    type,
    description,
    updatedBy: 'system',
    updatedAt: new Date().toISOString(),
  };
}

export function SettingsList({ settings, category, canManage }: SettingsListProps) {
  const filtered =
    category === 'rate-limits'
      ? settings.filter((setting) => REQUIRED_RATE_LIMIT_SETTINGS.some((required) => required.key === setting.key))
      : category === 'registration'
        ? settings.filter((setting) => REQUIRED_REGISTRATION_SETTINGS.some((required) => required.key === setting.key))
      : settings.filter((setting) => setting.key.startsWith(`${category}.`));
  const filteredWithDefaults =
    category === 'security'
      ? REQUIRED_SECURITY_SETTINGS.map((required) => {
          const existing = filtered.find((setting) => setting.key === required.key);
          return existing ?? buildVirtualSetting(required.key, required.defaultValue, 'NUMBER', required.description);
        })
      : category === 'rate-limits'
        ? REQUIRED_RATE_LIMIT_SETTINGS.map((required) => {
            const existing = filtered.find((setting) => setting.key === required.key);
            return existing ?? buildVirtualSetting(required.key, required.defaultValue, 'NUMBER', required.description);
          })
        : category === 'registration'
          ? REQUIRED_REGISTRATION_SETTINGS.map((required) => {
              const existing = filtered.find((setting) => setting.key === required.key);
              return existing ?? buildVirtualSetting(required.key, required.defaultValue, required.type, required.description);
            })
        : filtered;

  const getNumericSettingValue = (key: string): number | null => {
    const target = filteredWithDefaults.find((setting) => setting.key === key);
    if (!target) return null;
    const numericValue = Number(target.value);
    return Number.isFinite(numericValue) ? numericValue : null;
  };

  const maxAttempts = category === 'security' ? getNumericSettingValue('security.login.maxAttempts') : null;
  const blockDuration =
    category === 'security' ? getNumericSettingValue('security.login.blockDurationMinutes') : null;
  const showAggressiveWarning =
    category === 'security' &&
    maxAttempts !== null &&
    blockDuration !== null &&
    blockDuration > 60 &&
    maxAttempts < 5;
  const showRateLimitNotice = category === 'rate-limits';

  if (filteredWithDefaults.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center text-sm text-gray-500">
        No hay configuraciones en esta categoria.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {showAggressiveWarning ? (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-amber-900">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <p className="text-sm font-medium">
              Advertencia: Un bloqueo largo con pocos intentos puede afectar a usuarios en oficinas compartidas.
            </p>
          </div>
        </div>
      ) : null}

      {showRateLimitNotice ? (
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-blue-900">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <p className="text-sm font-medium">
              Los cambios pueden tardar hasta 60 segundos en reflejarse en los limitadores del servidor.
            </p>
          </div>
        </div>
      ) : null}

      <div className={category === 'security' || category === 'rate-limits' ? 'grid gap-6 md:grid-cols-2' : 'space-y-6'}>
        {filteredWithDefaults.map((setting) => (
          <SettingCard key={setting.id} setting={setting} canEdit={canManage} />
        ))}
      </div>
    </div>
  );
}
